import { useState } from 'react';
import { Ruler, Home, Building2, Square, Grid3x3 } from 'lucide-react';
import { useSubmissionStore } from '@/stores/submissionStore';
import { useTranslation } from '@/lib/i18n';
import { computeSlope } from '@gorampe/calc-engine';
import { SectionCard } from './SectionCard';
import { FieldLabel } from '../ui/FieldLabel';
import { ChoiceButton } from '../ui/ChoiceButton';
import { BigNumberInput } from '../ui/BigNumberInput';
import { Alert } from '../ui/Alert';
import { InclinationChart } from '../InclinationChart';

export function Section01Measures() {
  const submission = useSubmissionStore((s) => s.submission);
  const patchConfig = useSubmissionStore((s) => s.patchConfig);
  const { t } = useTranslation();
  const [chartOpen, setChartOpen] = useState(false);

  const { config } = submission;
  const totalRunIn = (() => {
    const shape = config.shape;
    if (shape.kind === 'straight') return shape.totalRunIn;
    if (shape.kind === 'L90') return Math.max(shape.arm1RunIn, shape.arm2RunIn);
    if (shape.kind === 'U180') return Math.max(shape.arm1RunIn, shape.arm2RunIn);
    if (shape.kind === 'switchback') {
      return shape.segments.length > 0 ? Math.max(...shape.segments.map((s) => s.runIn)) : 24;
    }
    return 24; // freeform fallback
  })();
  const slope = computeSlope(config.heightIn, totalRunIn);

  return (
    <SectionCard num="01" title={t('section.01.title')} icon={<Ruler size={18} strokeWidth={2} />}>
      <FieldLabel>{t('measures.usage.label')}</FieldLabel>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <ChoiceButton
          selected={config.usage === 'res'}
          onClick={() => patchConfig({ usage: 'res' })}
          icon={<Home size={14} strokeWidth={2} />}
        >
          {t('measures.usage.res')}
        </ChoiceButton>
        <ChoiceButton
          selected={config.usage === 'com'}
          onClick={() => patchConfig({ usage: 'com', floor: 'plein' })}
          icon={<Building2 size={14} strokeWidth={2} />}
        >
          {t('measures.usage.com')}
        </ChoiceButton>
      </div>

      <FieldLabel htmlFor="height">{t('measures.height.label')}</FieldLabel>
      <BigNumberInput
        id="height"
        min={1}
        step={1}
        value={config.heightIn}
        onChange={(e) => patchConfig({ heightIn: Number(e.target.value) || 0 })}
      />

      <FieldLabel>{t('measures.floor.label')}</FieldLabel>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <ChoiceButton
          selected={config.floor === 'plein'}
          onClick={() => patchConfig({ floor: 'plein' })}
          icon={<Square size={14} strokeWidth={2} />}
        >
          {t('measures.floor.plein')}
        </ChoiceButton>
        <ChoiceButton
          selected={config.floor === 'mailles'}
          onClick={() => patchConfig({ floor: 'mailles' })}
          disabled={config.usage === 'com'}
          icon={<Grid3x3 size={14} strokeWidth={2} />}
        >
          {t('measures.floor.mailles')}
        </ChoiceButton>
      </div>

      {config.usage === 'com' && (
        <p className="text-xs text-[var(--t3,rgba(15,30,51,0.42))] mb-2">
          {t('measures.floor.com_locked')}
        </p>
      )}

      {(() => {
        switch (slope.flag) {
          case 'ok':
            return (
              <Alert kind="ok">
                {t('measures.slope.ok', { percent: slope.percent.toFixed(1) })}
              </Alert>
            );
          case 'acceptable-1-11':
          case 'acceptable-1-10':
            return (
              <Alert kind="warn">
                {t('measures.slope.warn', { percent: slope.percent.toFixed(1) })}
              </Alert>
            );
          case 'limit-1-9':
          case 'max-absolute':
            return (
              <Alert kind="err">
                {t('measures.slope.error', { percent: slope.percent.toFixed(1) })}
              </Alert>
            );
          case 'error':
            return (
              <Alert kind="err">
                {t('measures.slope.error', { percent: slope.percent.toFixed(1) })}
              </Alert>
            );
          default:
            return null;
        }
      })()}
      <button
        type="button"
        onClick={() => setChartOpen(true)}
        className="mt-2 text-xs underline font-semibold text-[var(--brand-primary,#22D3EE)] hover:text-[var(--brand-primary-hover,#06B6D4)]"
      >
        {t('measures.slope.view_chart')}
      </button>
      <InclinationChart
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        currentHeightIn={config.heightIn}
        currentRunIn={totalRunIn}
      />
    </SectionCard>
  );
}
